/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Paper,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Alert,
} from '@mui/material';
import CustomInputTextField from 'components/shared/custom_input_textfield';

export interface Step2SimData {
  referenceYear: number;
  hasFamilyFarmersMarket: boolean | null;
  numberOfFamilyFarmers: number;
  annualMarketValue: number;
  totalFamilyFarmingValue: number;
  numberOfPermanentMarkets: number;
  numberOfNonPermanentMarkets: number;
}

interface Step2Props {
  onNext: () => void;
  onChange: (data: Step2SimData) => void;
  data: Step2SimData;
  disabled?: boolean;
  onBack?: () => void;
}

const Step2: React.FC<Step2Props> = ({
  onNext,
  onChange,
  data,
  disabled = false,
  onBack,
}) => {
  const currentYear = new Date().getFullYear();

  const [referenceYear] = useState<number>(data.referenceYear || currentYear);
  const [hasFamilyFarmersMarket, setHasFamilyFarmersMarket] = useState<boolean | null>(data.hasFamilyFarmersMarket ?? null);
  const [numberOfFamilyFarmers, setNumberOfFamilyFarmers] = useState<number>(data.numberOfFamilyFarmers ?? 0);
  const [annualMarketValue, setAnnualMarketValue] = useState<number>(data.annualMarketValue ?? 0);
  const [totalFamilyFarmingValue, setTotalFamilyFarmingValue] = useState<number>(data.totalFamilyFarmingValue ?? 0);
  const [numberOfPermanentMarkets, setNumberOfPermanentMarkets] = useState<number>(data.numberOfPermanentMarkets ?? 0);
  const [numberOfNonPermanentMarkets, setNumberOfNonPermanentMarkets] = useState<number>(data.numberOfNonPermanentMarkets ?? 0);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setHasFamilyFarmersMarket(data.hasFamilyFarmersMarket ?? null);
    setNumberOfFamilyFarmers(data.numberOfFamilyFarmers ?? 0);
    setAnnualMarketValue(data.annualMarketValue ?? 0);
    setTotalFamilyFarmingValue(data.totalFamilyFarmingValue ?? 0);
    setNumberOfPermanentMarkets(data.numberOfPermanentMarkets ?? 0);
    setNumberOfNonPermanentMarkets(data.numberOfNonPermanentMarkets ?? 0);
  }, [data]);

  useEffect(() => {
    onChange({
      referenceYear,
      hasFamilyFarmersMarket,
      numberOfFamilyFarmers,
      annualMarketValue,
      totalFamilyFarmingValue,
      numberOfPermanentMarkets,
      numberOfNonPermanentMarkets,
    });
    // eslint-disable-next-line
  }, [
    referenceYear,
    hasFamilyFarmersMarket,
    numberOfFamilyFarmers,
    annualMarketValue,
    totalFamilyFarmingValue,
    numberOfPermanentMarkets,
    numberOfNonPermanentMarkets,
  ]);

  const handleCurrencyChange = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    setter(Number(raw) / 100);
  };

  const formatCurrency = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const handleSubmit = () => {
    const newErrors: { [key: string]: string } = {};

    if (hasFamilyFarmersMarket) {
      if (!numberOfFamilyFarmers || numberOfFamilyFarmers < 1) newErrors.numberOfFamilyFarmers = 'Informe um número válido.';
      if (!annualMarketValue || annualMarketValue < 1) newErrors.annualMarketValue = 'Informe um valor válido.';
      if (!totalFamilyFarmingValue || totalFamilyFarmingValue < 1) newErrors.totalFamilyFarmingValue = 'Informe um valor válido.';
      if (numberOfPermanentMarkets < 0 || numberOfPermanentMarkets === null || numberOfPermanentMarkets === undefined) newErrors.numberOfPermanentMarkets = 'Informe um número válido.';
      if (numberOfNonPermanentMarkets < 0 || numberOfNonPermanentMarkets === null || numberOfNonPermanentMarkets === undefined) newErrors.numberOfNonPermanentMarkets = 'Informe um número válido.';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext();
    }
  };

  let activeValue = '';

  if (hasFamilyFarmersMarket === null) {
    activeValue = '';
  } else if (hasFamilyFarmersMarket) {
    activeValue = 'sim';
  } else {
    activeValue = 'nao';
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              O município possui feira da agricultura familiar?
            </Typography>
            <RadioGroup
              row
              value={activeValue}
              onChange={e => setHasFamilyFarmersMarket(e.target.value === 'sim')}
              name="has-family-farmers-market"
            >
              <FormControlLabel value="sim" control={<Radio />} label="Sim" />
              <FormControlLabel value="nao" control={<Radio />} label="Não" />
            </RadioGroup>
          </Grid>
        </Grid>
      </Paper>

      {hasFamilyFarmersMarket && (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CustomInputTextField
                label="Quantos agricultores(as) comercializam nas feiras?"
                type="number"
                inputProps={{ min: 1 }}
                value={numberOfFamilyFarmers}
                onChange={e => setNumberOfFamilyFarmers(Number(e.target.value))}
                error={errors}
                helperText={errors}
                fullWidth
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Qual o valor comercializado anualmente somente nas feiras?"
                value={formatCurrency(annualMarketValue)}
                onChange={handleCurrencyChange(setAnnualMarketValue)}
                error={!!errors.annualMarketValue}
                helperText={errors.annualMarketValue}
                fullWidth
                margin="dense"
                required
                inputProps={{ inputMode: 'numeric' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Qual o valor comercializado anualmente pela agricultura familiar?"
                value={formatCurrency(totalFamilyFarmingValue)}
                onChange={handleCurrencyChange(setTotalFamilyFarmingValue)}
                error={!!errors.totalFamilyFarmingValue}
                helperText={errors.totalFamilyFarmingValue}
                fullWidth
                margin="dense"
                required
                inputProps={{ inputMode: 'numeric' }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
                Quantas feiras existem no município? <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CustomInputTextField
                    label="Permanentes"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={numberOfPermanentMarkets}
                    onChange={e => setNumberOfPermanentMarkets(Number(e.target.value))}
                    error={errors}
                    helperText={errors.numberOfPermanentMarkets}
                    fullWidth
                    margin="dense"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CustomInputTextField
                    label="Livres"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={numberOfNonPermanentMarkets}
                    onChange={e => setNumberOfNonPermanentMarkets(Number(e.target.value))}
                    error={errors}
                    helperText={errors.numberOfNonPermanentMarkets}
                    fullWidth
                    margin="dense"
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={disabled}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          size="large"
          disabled={disabled}
        >
          Salvar e continuar
        </Button>
      </Box>
    </Box>
  );
};

export default Step2;
